import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatDate, formatCOP, formatApiError } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import ReviewerChecklistPanel from "../components/ReviewerChecklistPanel";
import { Disclaimer } from "../components/Disclaimer";

const STATE_LABEL = {
  nivel1_paid: "Pago confirmado",
  nivel1_processing: "Generando diagnóstico",
  nivel1_needs_human_review: "Listo para revisión",
  nivel1_revision_requested: "Revisión solicitada",
  nivel1_delivered: "Diagnóstico entregado",
};

function Pill({ tone, children, testid }) {
  const tones = {
    ok: "bg-emerald-50 text-emerald-800 border-emerald-300",
    warn: "bg-amber-50 text-amber-900 border-amber-300",
    err: "bg-rose-50 text-rose-800 border-rose-300",
    info: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <span data-testid={testid} className={`inline-flex border px-2 py-[3px] text-[11px] uppercase tracking-wider ${tones[tone] || tones.info}`}>
      {children}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6 border border-gray-200 bg-white p-6">
      <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">{title}</p>
      <div className="text-sm text-gray-800 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function ConfidenceBadge({ level }) {
  const tone = level === "alta" ? "ok" : level === "media" ? "warn" : "err";
  return <Pill tone={tone}>Confianza {level}</Pill>;
}

function DiagnosisViewer({ doc }) {
  if (!doc) return null;
  return (
    <div className="space-y-4" data-testid="diagnosis-viewer">
      <Section title="Resumen ejecutivo">
        <p>{doc.executive_summary?.content}</p>
        <ConfidenceBadge level={doc.executive_summary?.confidence} />
      </Section>

      <Section title="Clasificación preliminar">
        <p><strong>{doc.preliminary_classification}</strong></p>
      </Section>

      <Section title="Modalidad sugerida">
        <p><strong>{doc.preliminary_modality?.modalidad_sugerida}</strong></p>
        <p className="text-gray-700">{doc.preliminary_modality?.justificacion}</p>
        <ConfidenceBadge level={doc.preliminary_modality?.confidence} />
      </Section>

      {doc.missing_data?.length > 0 && (
        <Section title="Datos faltantes / ambiguos">
          <ul className="list-disc pl-5">
            {doc.missing_data.map((m, i) => <li key={`miss-${i}`}>{m}</li>)}
          </ul>
        </Section>
      )}

      <Section title="Análisis de comparables">
        <p>{doc.comparable_analysis?.content}</p>
        <ConfidenceBadge level={doc.comparable_analysis?.confidence} />
        {doc.comparable_insights?.length > 0 && (
          <ul className="mt-3 space-y-2 list-disc pl-5">
            {doc.comparable_insights.map((ci, i) => (
              <li key={`itm-${i}`}>
                <strong>{ci.entidad}</strong> — {ci.insight}{" "}
                <a href={ci.source_url} className="text-primary underline text-xs" target="_blank" rel="noopener noreferrer">fuente ↗</a>
                {ci.valor_cop != null && <span className="text-xs text-gray-500"> · {formatCOP(ci.valor_cop)}</span>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Análisis de sector">
        <p>{doc.basic_sector_analysis?.content}</p>
        <ConfidenceBadge level={doc.basic_sector_analysis?.confidence} />
      </Section>

      {doc.supplier_references?.length > 0 && (
        <Section title="Proveedores referenciados">
          <ul className="list-disc pl-5">
            {doc.supplier_references.map((s, i) => (
              <li key={`itm-${i}`}>
                <strong>{s.nombre}</strong>{s.nit ? ` (NIT ${s.nit})` : ""} — {s.contratos_referenciados} contrato(s){" "}
                <a href={s.source_url} className="text-primary underline text-xs" target="_blank" rel="noopener noreferrer">fuente ↗</a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Riesgos preliminares">
        <ul className="space-y-3">
          {doc.preliminary_risks?.map((r, i) => (
            <li key={`itm-${i}`} className="border-l-2 border-amber-400 pl-3">
              <p><strong>{r.descripcion}</strong></p>
              <p className="text-xs text-gray-600">Probabilidad: {r.probabilidad} · Impacto: {r.impacto}</p>
              <p className="text-gray-700">Mitigación: {r.mitigacion}</p>
              <ConfidenceBadge level={r.confidence} />
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Próximo paso recomendado">
        <p>{doc.recommended_next_step}</p>
      </Section>

      <Section title="Fuentes">
        <ul className="list-disc pl-5">
          {doc.sources?.map((s, i) => (
            <li key={`itm-${i}`}>
              <span className="text-xs uppercase tracking-wider text-gray-500 mr-2">{s.tipo}</span>
              {s.referencia}
              {s.url && <a href={s.url} className="text-primary underline text-xs ml-2" target="_blank" rel="noopener noreferrer">↗</a>}
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Supuestos y limitaciones">
        <p className="font-medium">Supuestos:</p>
        <ul className="list-disc pl-5 mb-3">
          {doc.assumptions?.map((a, i) => <li key={`itm-${i}`}>{a}</li>)}
        </ul>
        <p className="font-medium">Limitaciones:</p>
        <ul className="list-disc pl-5">
          {doc.limitations?.map((l, i) => <li key={`itm-${i}`}>{l}</li>)}
        </ul>
      </Section>

      <div className="border border-gray-200 bg-gray-50 p-5 text-xs text-gray-700 italic" data-testid="diagnosis-disclaimer">
        {doc.disclaimer}
      </div>

      {doc.meta && (
        <p className="text-[11px] text-gray-400 text-right">
          Generado con {doc.meta.model_used} · {doc.meta.latency_ms ? `${doc.meta.latency_ms} ms · ` : ""}
          costo aprox USD {doc.meta.cost_usd_estimated}
        </p>
      )}
    </div>
  );
}


function ChangeTypeBadge({ type }) {
  const map = {
    added: { tone: "ok", label: "Agregado" },
    removed: { tone: "err", label: "Eliminado" },
    modified: { tone: "warn", label: "Modificado" },
  };
  const cfg = map[type] || { tone: "info", label: type };
  return <Pill tone={cfg.tone}>{cfg.label}</Pill>;
}

function DiffValue({ label, value, testid }) {
  const [expanded, setExpanded] = useState(false);
  if (value === null || value === undefined) {
    return (
      <div className="text-xs text-gray-400 italic" data-testid={testid}>
        <span className="text-gray-500 mr-1">{label}:</span> —
      </div>
    );
  }
  const text = typeof value === "string" ? value : JSON.stringify(value);
  const long = text.length > 140;
  const shown = expanded || !long ? text : text.slice(0, 140) + "…";
  return (
    <div className="text-xs text-gray-800" data-testid={testid}>
      <span className="text-gray-500 mr-1">{label}:</span>
      <span className="whitespace-pre-wrap font-mono break-words">{shown}</span>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="ml-2 text-primary underline text-[11px]"
        >
          {expanded ? "ocultar" : "expandir"}
        </button>
      )}
    </div>
  );
}

function VersionDiffPanel({ caseId, versions, currentVersion }) {
  // Solo aplicable si existen ≥2 versiones y el caso actual tiene version > 1.
  // versions: [{version, is_current, ...}, ...]
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);
  const [toVersion, setToVersion] = useState(currentVersion || sortedVersions[0]?.version);
  const [fromVersion, setFromVersion] = useState(() => {
    const candidate = (currentVersion || sortedVersions[0]?.version || 2) - 1;
    return candidate >= 1 ? candidate : 1;
  });
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const compare = useCallback(async () => {
    setError("");
    if (fromVersion === toVersion) {
      setError("Selecciona dos versiones diferentes.");
      setDiff(null);
      return;
    }
    const fv = Math.min(fromVersion, toVersion);
    const tv = Math.max(fromVersion, toVersion);
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/cases/${caseId}/nivel1/diagnosis/diff?from_version=${fv}&to_version=${tv}`,
      );
      setDiff(data);
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || "No se pudo calcular el diff";
      setError(msg);
      setDiff(null);
    } finally {
      setLoading(false);
    }
  }, [caseId, fromVersion, toVersion]);

  return (
    <div className="mt-8 border border-gray-200 bg-white p-5" data-testid="version-diff-panel">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-gray-500">Comparar versiones</p>
          <h3 className="font-serif text-lg text-gray-900">Diff entre versiones del diagnóstico</h3>
          <p className="text-xs text-gray-500 mt-1">
            Útil para validar qué cambió la re-generación del LLM.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Desde</label>
            <Select
              value={String(fromVersion)}
              onValueChange={(v) => setFromVersion(Number(v))}
            >
              <SelectTrigger className="w-28 rounded-sm h-9" data-testid="diff-from-version">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortedVersions.map((v) => (
                  <SelectItem key={`from-${v.version}`} value={String(v.version)}>
                    v{v.version}{v.is_current ? " (actual)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Hasta</label>
            <Select
              value={String(toVersion)}
              onValueChange={(v) => setToVersion(Number(v))}
            >
              <SelectTrigger className="w-28 rounded-sm h-9" data-testid="diff-to-version">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortedVersions.map((v) => (
                  <SelectItem key={`to-${v.version}`} value={String(v.version)}>
                    v{v.version}{v.is_current ? " (actual)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={compare}
            disabled={loading || fromVersion === toVersion}
            className="rounded-sm h-9"
            data-testid="diff-compare-button"
          >
            {loading ? "Calculando…" : "Comparar"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="border border-rose-300 bg-rose-50 text-rose-900 px-3 py-2 text-sm mb-3" data-testid="diff-error">
          {error}
        </div>
      )}

      {!diff && !error && !loading && (
        <p className="text-xs text-gray-500" data-testid="diff-empty-state">
          Selecciona las versiones a comparar y presiona "Comparar".
        </p>
      )}

      {diff && (
        <div data-testid="diff-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border border-gray-200 mb-4 text-center">
            <div className="p-3 border-r border-gray-200">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Cambios</div>
              <div className="font-serif text-2xl text-gray-900" data-testid="diff-total-changes">
                {diff.summary?.total_changes ?? 0}
              </div>
            </div>
            <div className="p-3 border-r border-gray-200">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Modificados</div>
              <div className="font-serif text-2xl text-amber-700">{diff.summary?.fields_modified ?? 0}</div>
            </div>
            <div className="p-3 border-r border-gray-200">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Agregados</div>
              <div className="font-serif text-2xl text-emerald-700">{diff.summary?.items_added ?? 0}</div>
            </div>
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Eliminados</div>
              <div className="font-serif text-2xl text-rose-700">{diff.summary?.items_removed ?? 0}</div>
            </div>
          </div>

          {(diff.changes || []).length === 0 ? (
            <p className="text-xs text-gray-500 italic" data-testid="diff-no-changes">
              Las dos versiones son idénticas (no se encontraron diferencias).
            </p>
          ) : (
            <ul className="space-y-3">
              {diff.changes.map((c, i) => (
                <li
                  key={`itm-${i}`}
                  className="border border-gray-200 bg-gray-50 p-3"
                  data-testid={`diff-change-${i}`}
                >
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <ChangeTypeBadge type={c.change_type} />
                    <code className="font-mono text-xs text-gray-700 break-all">{c.path}</code>
                  </div>
                  <div className="space-y-1">
                    <DiffValue label="antes" value={c.from_value} testid={`diff-from-${i}`} />
                    <DiffValue label="después" value={c.to_value} testid={`diff-to-${i}`} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


export default function AdminCaseDiagnosisPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    try {
      const endpoint = isAdmin
        ? `/admin/cases/${id}/nivel1/diagnosis`
        : `/reviewer/cases/${id}/diagnosis`;
      const { data: d } = await api.get(endpoint);
      // Reviewer endpoint returns the report doc directly. Normaliza shape.
      if (!isAdmin && d?.validated_output) {
        setData({ current: d, case_state: null, versions: [d] });
      } else {
        setData(d);
      }
    } catch (e) {
      if (e?.response?.status === 404 && !isAdmin) {
        setData({ current: null, case_state: null, versions: [] });
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo cargar");
      }
    } finally {
      setLoading(false);
    }
  }, [id, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const onGenerate = async (force = false) => {
    setActing(true);
    try {
      await api.post(`/admin/cases/${id}/nivel1/generate`, { force });
      toast.success("Generación iniciada en background.");
      setPolling(true);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo iniciar");
    } finally {
      setActing(false);
    }
  };

  useEffect(() => {
    if (!polling) return;
    const iv = setInterval(async () => {
      await load();
      if (data?.case_state === "nivel1_needs_human_review" || data?.last_generation_error) {
        setPolling(false);
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [polling, load, data]);

  if (loading) return <div className="container-prose py-12"><div className="border border-gray-200 p-8 text-sm text-gray-500">Cargando…</div></div>;

  const current = data?.current;
  const validated = current?.validated_output;
  const versions = data?.versions || [];
  const err = data?.last_generation_error;
  const cstate = data?.case_state;

  return (
    <div data-testid="admin-case-diagnosis-page" className="container-prose py-12">
      <div className="max-w-4xl mx-auto">
        <Link to={isAdmin ? "/admin" : "/reviewer"} className="text-sm text-gray-500 hover:text-primary">← Volver</Link>
        <div className="mt-3 mb-8 pb-6 border-b border-gray-200">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">Diagnóstico</p>
          <h1 className="font-serif text-3xl text-gray-900 mb-2">Caso · {id.slice(0, 8)}</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {cstate && (
              <Pill tone={
                cstate === "nivel1_needs_human_review" ? "ok"
                  : cstate === "nivel1_processing" ? "warn"
                  : err ? "err" : "info"
              } testid={`state-${cstate}`}>
                {STATE_LABEL[cstate] || cstate}
              </Pill>
            )}
            {current?.version && <span className="text-gray-500">Versión actual: {current.version}</span>}
            {current?.created_at && <span className="text-gray-500">Generado: {formatDate(current.created_at)}</span>}
          </div>
        </div>

        {/* Reviewer panel: checklist + acciones */}
        {!isAdmin && (
          <div className="mb-8" data-testid="reviewer-panel-wrapper">
            <ReviewerChecklistPanel
              caseId={id}
              caseState={cstate || data?.current?.case_state}
              onApproved={() => load()}
              onChangesRequested={() => load()}
            />
          </div>
        )}

        {/* Admin controls */}
        {isAdmin && (
          <div className="mb-6">
            {err && (
              <div className="mb-4 border border-rose-300 bg-rose-50 text-rose-900 px-4 py-3 text-sm" data-testid="generation-error">
                <p className="font-medium">Error en última generación ({err.error_type})</p>
                <p className="text-xs italic">{err.error_message}</p>
                <p className="text-[11px] mt-1">{err.timestamp}</p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              {cstate === "nivel1_paid" && (
                <Button onClick={() => onGenerate(false)} disabled={acting} className="rounded-sm h-10" data-testid="generate-button">
                  {acting ? "Iniciando…" : "Generar diagnóstico"}
                </Button>
              )}
              {(cstate === "nivel1_processing" || err) && (
                <Button onClick={() => onGenerate(false)} disabled={acting} variant="outline" className="rounded-sm border-gray-300 h-10" data-testid="retry-button">
                  {acting ? "Reintentando…" : "Reintentar generación"}
                </Button>
              )}
              {validated && (
                <Button onClick={() => onGenerate(true)} disabled={acting} variant="outline" className="rounded-sm border-gray-300 h-10" data-testid="regenerate-force-button">
                  Re-generar (force, nueva versión)
                </Button>
              )}
              {polling && <span className="text-xs text-gray-500" data-testid="polling-indicator">Refrescando estado…</span>}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!validated && !err && (
          <div className="border border-gray-200 bg-gray-50 p-10 text-center" data-testid="diagnosis-empty">
            <p className="font-serif text-xl text-gray-900 mb-2">Sin diagnóstico generado</p>
            <p className="text-sm text-gray-600">Tras la confirmación del pago, la generación se dispara automáticamente. Si quedó en error, usa "Reintentar".</p>
          </div>
        )}

        {/* Viewer */}
        {validated && <DiagnosisViewer doc={validated} />}

        {/* Warnings */}
        {current?.validation_warnings?.length > 0 && (
          <div className="mt-6 border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600" data-testid="validation-warnings">
            <p className="font-medium mb-1">Warnings del guardrail ({current.validation_warnings.length})</p>
            <ul className="list-disc pl-5">
              {current.validation_warnings.slice(0, 10).map((w, i) => <li key={`itm-${i}`}>{w}</li>)}
            </ul>
          </div>
        )}

        {/* Version diff panel — Fase 9.1 Parte 3.
            Disponible para admin y reviewer asignado cuando hay ≥2 versiones. */}
        {versions.length > 1 && (
          <VersionDiffPanel
            caseId={id}
            versions={versions}
            currentVersion={current?.version}
          />
        )}

        {/* Version history */}
        {versions.length > 1 && (
          <div className="mt-8 border border-gray-200 bg-white p-5">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-3">Historial de versiones</p>
            <ul className="space-y-1 text-sm">
              {versions.map((v) => (
                <li key={v.id} className="flex items-center gap-3">
                  <span className="font-mono text-xs">v{v.version}</span>
                  <span className="text-gray-500 text-xs">{formatDate(v.created_at)}</span>
                  <span className="text-gray-700 text-xs">{v.model_used}</span>
                  {v.is_current && <Pill tone="ok">Actual</Pill>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Disclaimer obligatorio en toda página de output de caso (Fase 1 G2 + Fase 7) */}
        <div className="mt-10">
          <Disclaimer dataTestId="diagnosis-page-disclaimer" />
        </div>
      </div>
    </div>
  );
}
